/**
 * <ShipModal />
 *
 * Phase 18 — seller marks an order as SHIPPED with carrier + optional
 * tracking number.
 *
 * Backend contract:
 *   POST /api/orders/{id}/ship
 *   Body: { carrier, tracking_number?, tracking_url? }
 *   Returns: full updated Order
 *
 * For known carriers (UPS / USPS / FedEx / DHL) the backend
 * auto-generates the public tracking URL when a tracking number is
 * supplied. The seller can paste a custom URL only when carrier =
 * "Other".
 */
import * as React from "react";
import { Loader2 } from "lucide-react";
import { api, extractApiError } from "../../lib/api";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";

const CARRIERS = ["UPS", "USPS", "FedEx", "DHL", "Other"] as const;
type Carrier = (typeof CARRIERS)[number];

interface Props {
  open: boolean;
  onClose: () => void;
  orderId: string;
  /** Called with the updated Order on success so the parent can refresh. */
  onShipped: (order: any) => void;
}

export function ShipModal({ open, onClose, orderId, onShipped }: Props) {
  const [carrier, setCarrier] = React.useState<Carrier>("UPS");
  const [tracking, setTracking] = React.useState("");
  const [customUrl, setCustomUrl] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Reset when the modal opens on a new order.
  React.useEffect(() => {
    if (open) {
      setCarrier("UPS");
      setTracking("");
      setCustomUrl("");
      setError(null);
    }
  }, [open, orderId]);

  const handleSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload: any = { carrier };
      if (tracking.trim()) payload.tracking_number = tracking.trim();
      if (carrier === "Other" && customUrl.trim())
        payload.tracking_url = customUrl.trim();
      const res = await api.post(`/orders/${orderId}/ship`, payload);
      onShipped(res.data);
      onClose();
    } catch (e) {
      setError(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Mark as Shipped"
      description="Pick a carrier and (optionally) a tracking number. The buyer gets an email notification."
      testid="ship-modal"
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-semibold text-ink mb-1">
            Carrier
          </label>
          <select
            value={carrier}
            onChange={(e) => setCarrier(e.target.value as Carrier)}
            data-testid="ship-carrier-select"
            className="w-full h-12 rounded-xl border-2 border-surface-border bg-white px-3 text-ink focus:border-ink focus:outline-none"
          >
            {CARRIERS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-ink mb-1">
            Tracking number{" "}
            <span className="text-ink-muted font-normal">(optional)</span>
          </label>
          <input
            type="text"
            data-testid="ship-tracking-input"
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            maxLength={100}
            placeholder="e.g. 1Z999AA10123456784"
            className="w-full rounded-xl border-2 border-surface-border bg-white p-3 text-ink focus:border-ink focus:outline-none"
          />
        </div>

        {carrier === "Other" && (
          <div>
            <label className="block text-sm font-semibold text-ink mb-1">
              Tracking URL{" "}
              <span className="text-ink-muted font-normal">(optional)</span>
            </label>
            <input
              type="url"
              data-testid="ship-tracking-url-input"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              maxLength={500}
              placeholder="https://carrier.example/track?id=..."
              className="w-full rounded-xl border-2 border-surface-border bg-white p-3 text-ink focus:border-ink focus:outline-none"
            />
            <p className="text-xs text-ink-muted mt-1">
              Required only when buyers should be able to click through to your
              carrier&apos;s tracking page.
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="ship-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={busy}
            data-testid="ship-submit"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Marking shipped…
              </>
            ) : (
              "Mark as Shipped"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
